import IconFilters from '@assets/icons/filters.svg?react';
import { Box, IconButton, Popover, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { usePage } from '../../../contexts/usePageContext';
import { setConfigStorageItem } from '../../../hooks/useConfigStorage';
import {
 changeCurrentPreset,
 ColPresetType,
} from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { StructureType } from '../../../types/types';
import { findStructure, getTranslation } from '../../../utils/dataTypes';
import { normalizeSearch } from '../../../utils/stringUtils';
import FieldSearch from '../FieldSearch';

interface FilterColumnsPopoverProps {
 anchorEl: HTMLElement | null;
 onClose: () => void;
 currentColumnsPreset: ColPresetType | null;
 structure: StructureType[];
}

const FilterColumnsPopover = ({
 anchorEl,
 onClose,
 currentColumnsPreset,
 structure,
}: FilterColumnsPopoverProps) => {
 const th = useTheme();
 const dispatch = useDispatch();
 const { pageName } = usePage();
 const { t } = useTranslation();
 const [searchValue, setSearchValue] = useState('');

 const open = Boolean(anchorEl);

 const toggleFilter = (field: string) => {
  const filterActive =
   currentColumnsPreset?.filters?.fields?.hasOwnProperty(field);
  const newFilters = { ...currentColumnsPreset?.filters?.fields };
  const newOperators = { ...currentColumnsPreset?.filters?.operators };

  if (filterActive) {
   delete newFilters[field];
   delete newOperators[field];

   const currentStructure = findStructure(structure, field);
   if (currentStructure?.Type === 'datetime') {
    delete newFilters.DateField;
    delete newFilters.DateFrom;
    delete newFilters.DateTo;
    delete newOperators.DateField;
    delete newOperators.DateFrom;
    delete newOperators.DateTo;
   }
  } else {
   newFilters[field] = '';
   newOperators[field] = '';
  }

  const preset = {
   ...currentColumnsPreset,
   name: currentColumnsPreset?.name || '',
   columns: currentColumnsPreset?.columns || [],
   filters: { fields: newFilters, operators: newOperators },
  };

  dispatch(
   changeCurrentPreset({
    page: pageName,
    type: 'columns',
    preset,
    temp: true,
   }),
  );
  dispatch(
   changeCurrentPreset({
    page: pageName,
    type: 'columns',
    preset,
    temp: false,
   }),
  );
  setConfigStorageItem({ page: pageName, key: 'columns', value: preset });
 };

 const handleClose = () => {
  setSearchValue('');
  onClose();
 };

 const filteredColumns = (currentColumnsPreset?.columns || []).filter((col) => {
  if (!searchValue) return true;
  const translation = getTranslation(structure, col.field);
  return normalizeSearch(translation).includes(normalizeSearch(searchValue));
 });

 return (
  <Popover
   open={open}
   anchorEl={anchorEl}
   onClose={handleClose}
   elevation={0}
   anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
   transformOrigin={{ vertical: 'top', horizontal: 'right' }}
   slotProps={{
    paper: {
     sx: {
      width: 280,
      maxHeight: 400,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: (theme) => theme.palette.kemek.lightBlue,
      border: (theme) => `1px solid ${theme.palette.kemek.lightestGray}`,
      p: 1,
     },
    },
   }}
  >
   <Box
    sx={{
     border: (theme) => `1px solid ${theme.palette.kemek.lightestGray}`,
     background: (theme) => theme.palette.kemek.white,
     borderRadius: '4px',
     display: 'flex',
     flexDirection: 'column',
     minHeight: 0,
     flex: 1,
    }}
   >
    <FieldSearch
     searchValue={searchValue}
     setSearchValue={setSearchValue}
     label={t('global.search')}
    />
    <Box
     sx={{
      flex: 1,
      overflowY: 'auto',
      minHeight: 0,
      py: '4px',
     }}
    >
     {filteredColumns.map((col) => {
      const hasFilter = currentColumnsPreset?.filters?.fields?.hasOwnProperty(
       col.field,
      );
      const translation = getTranslation(structure, col.field);

      return (
       <Box
        key={col.field}
        sx={{
         display: 'flex',
         alignItems: 'center',
         justifyContent: 'space-between',
         px: '12px',
         py: '4px',
         '&:hover': { backgroundColor: th.palette.action.hover },
        }}
       >
        <Typography sx={{ fontSize: 14 }}>{translation}</Typography>
        <IconButton
         size="small"
         onClick={() => toggleFilter(col.field)}
         aria-label={
          hasFilter
           ? `${t('global.disable')} ${translation}`
           : `${t('global.enable')} ${translation}`
         }
         tabIndex={0}
        >
         <IconFilters
          style={{
           color: hasFilter
            ? th.palette.kemek.blue
            : th.palette.kemek.lightestGray,
          }}
         />
        </IconButton>
       </Box>
      );
     })}
    </Box>
   </Box>
  </Popover>
 );
};

export default FilterColumnsPopover;
